import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {HttpParams} from '@angular/common/http';
import {forkJoin, Observable, of as observableOf} from 'rxjs';
import {map} from 'rxjs/internal/operators';
import {BaseService} from './base.service';
import {Endpoints} from '../config/endpoints';
import {User} from '../models/user';
import {APP_VERSION} from '../config/version';
import {base_urls} from '../config/base_urls';
import {SelfDetail, SelfDetailMarket, SelfDetailMarketLevel, SelfDetailOrganization} from '../models/self-detail';
import {UserRolePermissionList} from '../models/user-role-permission-list';

@Injectable({providedIn: 'root'})
export class AuthService {

    changePassword = false;

    constructor(private router: Router, private service: BaseService) {
    }

    get user(): User {
        return JSON.parse(localStorage.getItem('user') || null);
    }

    prepareUser(pSelfDetail: SelfDetail, pOrganization: SelfDetailOrganization,
                pMarketLevels: SelfDetailMarketLevel[], pUserModules: UserRolePermissionList[]): User {
        const user = new User();
        user.Active = pSelfDetail.isActive;
        user.AttendanceLat = pSelfDetail.attendanceLat;
        user.AttendanceLon = pSelfDetail.attendanceLon;
        user.Designation = pSelfDetail.designation;
        user.Id = pSelfDetail.id;
        user.MarketList = pSelfDetail.markets.map((pSelfDetailMarket: SelfDetailMarket) => ({
                Id: pSelfDetailMarket.id,
                MarketName: pSelfDetailMarket.name,
                MarketCode: pSelfDetailMarket.code,
                Active: pSelfDetailMarket.isActive,
                Deleted: false,
                MarketLevel: {
                    Id: pSelfDetailMarket.marketLevel.id,
                    Active: pSelfDetailMarket.marketLevel.isActive,
                    Deleted: false,
                    MarketLevelName: pSelfDetailMarket.marketLevel.name,
                    Rank: pSelfDetailMarket.marketLevel.levelRank
                },
                ParentMarketList: pSelfDetailMarket.parents.map((pParentMarket: SelfDetailMarket) => ({
                    Id: pParentMarket.id,
                    MarketName: pParentMarket.name,
                    MarketCode: pParentMarket.code,
                    Active: pParentMarket.isActive,
                    Deleted: false,
                    MarketLevel: {
                        Id: pParentMarket.marketLevel.id,
                        Active: pParentMarket.isActive,
                        Deleted: false,
                        MarketLevelName: pParentMarket.marketLevel.name,
                        Rank: pParentMarket.marketLevel.levelRank
                    },
                    ParentMarketList: []
                })) || []
            })
        ) || [];
        user.Mobile = pSelfDetail.mobile;
        user.ProductLineList = pSelfDetail.productLines.map((pProductLine) => ({
            Id: pProductLine.id,
            Active: pProductLine.isActive,
            Name: pProductLine.name,
            Deleted: false
        })) || [];
        user.UserName = pSelfDetail.name;
        user.UserCode = pSelfDetail.code;
        user.UserRole = {
            Id: pSelfDetail.role.id,
            Active: pSelfDetail.role.isActive,
            Deleted: false,
            UserRoleName: pSelfDetail.role.name,
            UserType: pSelfDetail.userType,
            MarketLevel: pSelfDetail.role.marketLevel ? {
                Id: pSelfDetail.role.marketLevel.id,
                Active: pSelfDetail.role.marketLevel.isActive,
                MarketLevelName: pSelfDetail.role.marketLevel.name,
                Rank: pSelfDetail.role.marketLevel.levelRank,
                Deleted: false
            } : null,
            ModuleList: pUserModules.length > 0 ? pUserModules.map((pPermission) => ({
                Id: null,
                Active: true,
                Deleted: false,
                ModuleName: pPermission.displayName,
                ModuleNameEnum: pPermission.name,
                ResourceList: pPermission.authorities.map((pAuthority) => ({
                    Id: null,
                    Active: true,
                    Deleted: false,
                    ResourceName: pAuthority.displayName,
                    ResourceEnum: pAuthority.name,
                    Url: pAuthority.authority
                }))
            })) : []
        };

        user.Organization = {
            Id: pOrganization.id,
            OrganizationCode: pOrganization.code,
            OrganizationName: pOrganization.name,
            Address: pOrganization.address,
            Active: pOrganization.isActive,
            Deleted: false
        };

        user.MarketLevelHierarchy = pMarketLevels.map((pMarketLevel) => ({
            Id: pMarketLevel.id,
            MarketLevelName: pMarketLevel.name,
            Rank: pMarketLevel.levelRank,
            Active: pMarketLevel.isActive,
            Deleted: false
        }));

        return user;
    }

    get isOrgAdmin(): boolean {
        let isOrgAdmin = false;
        if (this.user.UserRole.ModuleList.length > 0) {
            const [module] = this.user.UserRole.ModuleList.filter(pModule => pModule.ModuleNameEnum == 'ADMIN');
            if (module) {
                isOrgAdmin = module.ResourceList.filter(pResource => pResource.ResourceEnum == 'ADMIN_ACCESS').length > 0;
            }
        }
        return isOrgAdmin;
    }

    get isSuperAdmin(): boolean {
        let isSuperAdmin = false;
        if (this.user.UserRole.ModuleList.length == 0) {
            isSuperAdmin = true;
        }
        return isSuperAdmin;
    }

    getAccessToken(pUsername: string, pPassword: string): Observable<any> {
        return this.service.postData(Endpoints.login, new HttpParams({
            fromObject: {
                'username': pUsername,
                'password': pPassword,
                'grant_type': 'password',
                'client_uuid': this.service.uuid,
                'client_type': 'BROWSER',
                'app_version_name': APP_VERSION
            }
        }), false)
            .pipe(map(pTokenResponse => {
                if (pTokenResponse['access_token']) {
                    localStorage.setItem('access_token', pTokenResponse['access_token']);
                    localStorage.setItem('refresh_token', pTokenResponse['refresh_token']);
                    this.service.getData(base_urls.sfa_url + Endpoints.self).subscribe(pSelfResponse => {
                        localStorage.setItem('user', JSON.stringify(pSelfResponse['Data']['User']));
                        localStorage.setItem('user_password', pSelfResponse['Data']['User'].Password);
                        localStorage.setItem('user_changePassword', pSelfResponse['Data']['User'].ChangePassword ? pSelfResponse['Data']['User'].ChangePassword : null);
                        if (localStorage.getItem('user_changePassword') == 'true') {
                            window.location.href = 'auth/change-password';
                        } else {
                            window.location.href = 'dashboard';
                        }

                    });
                }
                return pTokenResponse;
            }));
    }

    getAccessTokenForMicroservice(pUsername: string, pPassword: string, pMicroserviceUrl: string): Observable<any> {
        return this.service.postDataForMicroService(pMicroserviceUrl + Endpoints.login_micro_service, new HttpParams({
            fromObject: {
                'username': pUsername,
                'password': pPassword,
                'grant_type': 'password',
                'client_uuid': this.service.uuid,
                'client_type': 'BROWSER',
                'app_version': APP_VERSION
            }
        }), false)
            .pipe(map(pTokenResponse => {
                if (pTokenResponse['access_token']) {
                    localStorage.setItem('access_token', pTokenResponse['access_token']);
                    localStorage.setItem('refresh_token', pTokenResponse['refresh_token']);

                    forkJoin([this.service.getDataForMicroService(base_urls.f_base + Endpoints.self_detail),
                        this.service.getDataForMicroService(base_urls.f_base + Endpoints.org_sync),
                        this.service.getDataForMicroService(base_urls.f_base + Endpoints.market_level),
                        this.service.getDataForMicroService(base_urls.f_base + Endpoints.self_authorities)]).subscribe(([pSelfDetail, pOrganization, pMarketLevels, pSelfPermissions]) => {
                        localStorage.setItem('user', JSON.stringify(this.prepareUser(pSelfDetail['data'], pOrganization['data'], pMarketLevels['data']['content'].reverse(), pSelfPermissions['data']['content'])));
                        window.location.href = 'dashboard';
                    });
                }
                return pTokenResponse;
            }));
    }

    refreshAccessToken(): Observable<any> {
        return this.service.postData(Endpoints.login, new HttpParams({
            fromObject: {
                'refresh_token': this.service.refreshToken,
                'grant_type': 'refresh_token'
            }
        }), false)
            .pipe(map(response => {
                if (response['access_token']) {
                    localStorage.setItem('access_token', response['access_token']);
                    localStorage.setItem('refresh_token', response['refresh_token']);
                }
                return observableOf(response);
            }));
    }

    logout(): Observable<boolean> {
        localStorage.removeItem('user_changePassword');
        localStorage.removeItem('user_password');
        return this.service.getData(Endpoints.logout, {}, true).pipe(map(response => {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            return true;
        }));

    }

    hasModuleAccess(pModuleNameEnum: string): boolean {
        let hasAccess = false;
        if (this.user.UserRole.ModuleList.length == 0) {
            hasAccess = true;
        } else {
            const [module] = this.user.UserRole.ModuleList.filter(pModule => pModule.ModuleNameEnum == pModuleNameEnum.toUpperCase());
            if (module) {
                hasAccess = true;
            }
        }

        return hasAccess;
    }

    hasAccess(pModuleNameEnum: string, pResourceEnum: string): boolean {
        let hasAccess = false;
        if (this.user.UserRole.ModuleList.length == 0) {
            hasAccess = true;
        } else {
            const [module] = this.user.UserRole.ModuleList.filter(pModule => pModule.ModuleNameEnum == pModuleNameEnum.toUpperCase());
            if (module) {
                hasAccess = module.ResourceList.filter(pAuthority => pAuthority.ResourceEnum == pResourceEnum.toUpperCase()).length > 0;
            }
        }

        return hasAccess;
    }
}
